import type {PropertyValues} from "lit";
import {LitElement, unsafeCSS} from "lit";
import {customElement, property} from "lit/decorators.js";
import {html} from "lit/static-html.js";
import {
  COLLAPSE_COMPONENTS_EVENT,
  CONTEXT_CHANGED_EVENT,
  ROOT_TAG
} from "./globals";
import "./i18n";
import style from "./App.css?inline";
import {RuntimeProvider} from "./runtime";
import type {Partition} from "@common-core/web-component-runtime/runtime";
import {BridgeBarDataProvider, MembershipDataProvider} from "./backend";
import {LaunchDarklyProvider} from "./launchdarkly";
import {BridgeBarConfigProvider} from "./config";
import type {ConfigUserLink, Language, Theme, UserDataEntry} from "./config";
import {BridgeBar, BBContainerElement} from "./components/container";
import {ApplicationSwitcher} from "./components/application-switcher";
import {SideNav} from "./components/side-nav";
import {OrgContextSelector} from "./components/org-context-selector";
import type {BridgeBarContextChangedDetail} from "./lib/contextSelection";
import {CompanyLogo} from "./components/company-logo";
import {NotificationsBell} from "./components/notifications";
import {UserProfileMenu} from "./components/user-profile-menu";
import {SupportMenu} from "./components/support-menu";

const tagName = ROOT_TAG;

/**
 * RootElement is the public API surface for the Bridge Bar web component.
 *
 * It accepts all configuration properties via @property() decorators and
 * delegates context provision to BridgeBarConfigProviderElement. The provider
 * chain order follows AD-2: ConfigProvider → RuntimeProvider →
 * BridgeBarDataProvider → LaunchDarklyProvider → slot.
 */
@customElement(tagName)
export class RootElement extends LitElement {
  static styles = unsafeCSS(style);

  @property()
  applicationId: string = "";

  @property()
  sessionId: string = "";
  /**
   * Internal use only
   */
  @property()
  deployment: string = "live";

  @property()
  partition: Partition = "prod";
  /**
   * @deprecated
   */
  @property()
  width: string = "";

  @property()
  theme: Theme = "light";

  @property()
  language: Language = "en";
  /**
   * @deprecated
   */
  @property({type: Array})
  userLinks: ConfigUserLink[] = [];
  /**
   * @deprecated
   */
  @property({type: Array})
  userData: UserDataEntry[] = [];
  /**
   * @deprecated
   */
  @property({attribute: false})
  dealerLabelHtml: string = "";
  /**
   * Internal use only
   */
  @property({attribute: false})
  useMockData: boolean = false;
  /**
   * @deprecated
   */
  @property({attribute: false})
  getTracker: (() => unknown) | undefined = undefined;

  /**
   * Adopter opt-in for the Org Context Selector. The selector renders only when
   * the adopter declares `common-org-entity`. Absent ⇒ the selector is not
   * shown for this app.
   */
  @property()
  contextType?: string;

  /**
   * Internal use only
   */
  @property()
  sidebarVariant: "fixed" | "float" = "fixed";

  override willUpdate(changed: PropertyValues): void {
    if (changed.has("theme")) {
      this.setAttribute("data-ids-theme-mode", this.theme);
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener(CONTEXT_CHANGED_EVENT, this.handleContextChanged);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(CONTEXT_CHANGED_EVENT, this.handleContextChanged);
  }

  /**
   * Re-dispatch the selector's composed context-change event as a single,
   * clean document-level event — the one channel both adopter integration
   * styles listen on (AD-8, F-10). The internal composed event is stopped at
   * the bar boundary so `document` does not also receive the original.
   */
  private handleContextChanged = (e: Event): void => {
    const detail = (e as CustomEvent<BridgeBarContextChangedDetail>).detail;
    e.stopPropagation();
    document.dispatchEvent(new CustomEvent(CONTEXT_CHANGED_EVENT, {detail}));
  };

  /**
   * Public API: collapse any open Bridge Bar components (application switcher,
   * user profile menu, etc.). Consumers call this on the
   * `<enterprise-product-experience-bridge-bar>` element — e.g. to dismiss
   * open menus when their own app opens a modal or navigates.
   *
   * Delegates to the container, which broadcasts the collapse request to its
   * children. Falls back to dispatching {@link COLLAPSE_COMPONENTS_EVENT}
   * directly if the container is not yet rendered, so the call never throws.
   */
  collapseComponents(): void {
    const container = this.shadowRoot?.querySelector(
      `${ROOT_TAG}-container`
    ) as BBContainerElement | null;
    if (container) {
      container.collapseComponents();
      return;
    }
    document.dispatchEvent(new CustomEvent(COLLAPSE_COMPONENTS_EVENT));
  }

  render() {
    return html`
      <${BridgeBarConfigProvider}
        applicationId=${this.applicationId}
        sessionId=${this.sessionId}
        deployment=${this.deployment}
        partition=${this.partition}
        width=${this.width}
        theme=${this.theme}
        language=${this.language}
        .userLinks=${this.userLinks}
        .userData=${this.userData}
        .dealerLabelHtml=${this.dealerLabelHtml}
        .useMockData=${this.useMockData}
        .getTracker=${this.getTracker}
        .contextType=${this.contextType}
        .sidebarVariant=${this.sidebarVariant}
      >
        <${RuntimeProvider}>
          <${BridgeBarDataProvider}>
            <${LaunchDarklyProvider}>
              <${BridgeBar}>
                <${SideNav} slot="side-nav"></${SideNav}>
                <${CompanyLogo} slot="logo"></${CompanyLogo}>
                <${ApplicationSwitcher} slot="switcher"></${ApplicationSwitcher}>
                <${MembershipDataProvider} slot="context-selector">
                  <${OrgContextSelector}></${OrgContextSelector}>
                </${MembershipDataProvider}>
                <${SupportMenu} slot="support-menu"></${SupportMenu}>
                <${NotificationsBell} slot="notifications"></${NotificationsBell}>
                <${UserProfileMenu} slot="avatar"></${UserProfileMenu}>
              </${BridgeBar}>
            </${LaunchDarklyProvider}>
          </${BridgeBarDataProvider}>
        </${RuntimeProvider}>
      </${BridgeBarConfigProvider}>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: RootElement;
  }
}
